import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { CtaBand } from "@/components/layout/CtaBand";
import { Icon } from "@/components/Icon";
import { IMAGES, CORE_VALUES } from "@/data/content";
import { Target, Eye } from "lucide-react";
import { FounderSection } from "@/components/FounderSection";
import { useContentText } from "@/context/SiteConfig";

export default function About() {
  useSeo({
    title: "About Us",
    description:
      "Meridian Vanguard Solutions is a veteran-led small business committed to connecting government agencies with dependable suppliers through ethical, mission-focused procurement.",
    path: "/about",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "About", path: "/about" }],
  });

  return (
    <>
      <PageHero
        eyebrow="Who We Are"
        titleLines={["About Meridian", "Vanguard Solutions"]}
        subtitle="A boutique government contracting partner built on discipline, integrity, and a commitment to long-term mission success."
        image={IMAGES.office}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
      />

      {/* Intro */}
      <section className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Our Story"
              title="A veteran-led approach to government procurement"
              description="Meridian Vanguard Solutions, LLC was founded to bring discipline, accountability, and service-oriented values to the way government agencies acquire products and services. We operate as a dependable intermediary between the public sector and a vetted network of commercial suppliers."
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-steel leading-relaxed text-lg">
                Our team combines procurement expertise with a genuine commitment to ethical
                business practices. We believe that reliable partnerships, transparent
                communication, and rigorous attention to compliance are what set a trusted
                contractor apart.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-xl">
              <img src={IMAGES.dashboard} alt="Procurement performance analytics" loading="lazy" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-navy/10 rounded-xl" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-lightgray py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-xl bg-white border border-hairline p-10">
              <div className="h-14 w-14 rounded-xl bg-navy flex items-center justify-center">
                <Target className="h-7 w-7 text-white" strokeWidth={1.6} />
              </div>
              <h3 className="mt-6 font-heading text-2xl font-semibold text-navy">Our Mission</h3>
              <p className="mt-4 text-steel leading-relaxed text-lg">
                {useContentText("about_mission", "To connect government agencies with dependable suppliers through strategic procurement, ethical business practices, and mission-focused execution that delivers measurable value.")}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-xl bg-white border border-hairline p-10">
              <div className="h-14 w-14 rounded-xl bg-navy flex items-center justify-center">
                <Eye className="h-7 w-7 text-white" strokeWidth={1.6} />
              </div>
              <h3 className="mt-6 font-heading text-2xl font-semibold text-navy">Our Vision</h3>
              <p className="mt-4 text-steel leading-relaxed text-lg">
                {useContentText("about_vision", "To become a trusted procurement partner recognized for excellence, integrity, and long-term strategic relationships across the public sector.")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Core values */}
      <section className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="What Guides Us" title="Core Values" />
          <StaggerGroup stagger={0.06} className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CORE_VALUES.map((v, i) => (
              <StaggerItem key={v}>
                <div className="group h-full rounded-xl border border-hairline bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-navy/20 hover:shadow-[0_14px_30px_rgba(10,35,66,0.08)]">
                  <span className="font-heading text-sm font-bold text-accent2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h4 className="mt-3 font-heading text-lg font-semibold text-navy">{v}</h4>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Veteran leadership band */}
      <section className="bg-lightgray py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-xl">
              <img src={IMAGES.cargoShip} alt="Cargo logistics and supply chain" loading="lazy" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Leadership"
              title="Discipline shaped by service"
              description="Our leadership reflects the accountability and mission orientation developed through service-oriented values. That perspective informs how we manage every engagement — with focus, reliability, and respect for the outcomes our clients need to achieve."
            />
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-white border border-hairline px-4 py-2 text-sm font-medium text-navy">
                  <Icon name="ShieldCheck" className="h-4 w-4 text-accent2" /> Veteran-Owned
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white border border-hairline px-4 py-2 text-sm font-medium text-navy">
                  <Icon name="Building2" className="h-4 w-4 text-accent2" /> Small Business
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white border border-hairline px-4 py-2 text-sm font-medium text-navy">
                  <Icon name="BadgeCheck" className="h-4 w-4 text-accent2" /> Compliance-Driven
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FounderSection />

      <CtaBand />
    </>
  );
}
